import {create} from "zustand"
import {persist} from "zustand/middleware"
import axios from "axios"
import {READ_ONLY_REFUSED, isReadOnlyRole, onConsoleUserChange, useAuthStore} from "./authStore"
import {getAuthHeaders, withAuth} from "../services/apiAuth"

interface Device {
	device_uid: string
	connection_status?: string
	createdAt?: string
	device_located?: boolean
	device_name?: string
	device_placed?: boolean
	device_short_ID?: string
	device_token?: string
	device_type?: string
	iot_core_registered_on?: string
	iot_core_thing_name?: string
	is_alarm?: boolean
	is_calibrated?: boolean
	is_enabled?: boolean
	is_registered?: boolean
	location_ID?: string
	ssid?: string
	updatedAt?: string
	user_ID?: string
}

interface DeviceConnectivity {
	timestamp: number
	macAddress: string
	status: "connected" | "disconnected"
}

interface FirmwareUploadResponse {
	message: string
	fileName: string
	originalFileName: string
	fileUrl: string
	uploadTimestamp: number
	fileSize: number
	jobId?: string
	deviceCount?: number
}

interface LatestFirmware {
	fileName: string
	version: string
	timestamp: number
	key: string
	macAddress?: string
}

interface JobStatus {
	macAddress: string
	deviceName: string | null
	deviceType: string | null
	status: string
	queuedAt: string | null
	startedAt: string | null
	lastUpdatedAt: string | null
	checkedAt: string
	error?: string
}

interface JobProgressResponse {
	jobId: string
	checkedAt: string
	summary: {
		total: number
		queued: number
		inProgress: number
		succeeded: number
		failed: number
		notFound: number
		error: number
	}
	devices: JobStatus[]
}

interface LambdaErrorResponse {
	error: string
	message?: string
	details?: string | object
	failedDevices?: Array<{
		macAddress: string
		deviceType: string
		fileName: string
		error: {
			type: string
			message: string
			details?: string | object
		}
	}>
	uploadedFiles?: object[]
	uploadedFile?: object
}

export type CiTarget = "angel" | "falcon"
export type CiBranch = "master" | "release"
export const CI_TARGETS: readonly CiTarget[] = ["angel", "falcon"]
export const CI_BRANCHES: readonly CiBranch[] = ["master", "release"]

// SSM command statuses of a console-started CI build that is not finished yet.
const CI_ACTIVE_STATUSES = ["Pending", "InProgress", "Delayed", "Cancelling"]
export const isCiBuildActive = (status?: string | null): boolean =>
	!!status && CI_ACTIVE_STATUSES.includes(status)

export interface CiBuildStatus {
	commandId: string
	target: CiTarget | string
	branch: CiBranch | string
	status: string
	requestedAt: string | null
	finishedAt: string | null
	outputTail: string
}

export interface CiBuildArtifact {
	key: string
	branch: CiBranch
	variant: "crc" | "no_crc"
	builtAt: string
	size: number
	lastModified: string | null
}

export type StartCiBuildResult =
	| {alreadyRunning: false; commandId: string; target: CiTarget; branch: CiBranch; status: string}
	| {alreadyRunning: true; commandId: string | null; message: string}

interface DevicesState {
	status: "loading" | "loaded" | "error" | "idle"
	devices: Device[]
	selectedDevice: Device | null
	error: string | null

	deviceConnectivity: Map<string, DeviceConnectivity>
	connectivityLoading: boolean
	connectivityError: string | null

	firmwareUploadLoading: boolean
	firmwareUploadError: string | null
	firmwareUploadSuccess: string | null
	firmwareErrorDismissed: boolean

	rtlUploadLoading: boolean
	rtlUploadError: string | null
	rtlUploadSuccess: string | null
	rtlErrorDismissed: boolean

	// Bumped when a console CI build finishes successfully, so the S3 build picker reloads its list.
	ciBuildFinishedAt: number | null

	latestFirmware: LatestFirmware | null
	latestFirmwareLoading: boolean
	deviceFirmwares: Map<string, LatestFirmware>

	jobProgress: JobProgressResponse | null
	jobProgressLoading: boolean
	jobProgressError: string | null
	currentJobId: string | null
	currentJobType: "firmware" | "rtl" | null
	jobProgressInterval: NodeJS.Timeout | null
	deviceUploadStatuses: Map<string, JobStatus>
	latestJobId: string | null

	getAllDevices: () => Promise<void>
	getDevicesByUserId: (userId: string) => Promise<void>
	getDeviceById: (deviceId: string) => Promise<void>
	setSelectedDevice: (device: Device | null) => void
	clearDevices: () => void
	clearError: () => void

	fetchDevicesConnectivity: (macAddresses: string[]) => Promise<void>
	getDeviceConnectionStatus: (deviceId: string) => "connected" | "disconnected" | "unknown"
	clearConnectivity: () => void

	uploadFirmware: (
		fileName: string,
		fileContent: string,
		fileType: string,
	) => Promise<FirmwareUploadResponse>
	uploadFirmwarePerDevice: (
		fileName: string,
		fileContent: string,
		fileType: string,
		deviceIds: string[],
	) => Promise<FirmwareUploadResponse>
	clearFirmwareUploadStatus: () => void

	uploadRtl: (
		fileName: string,
		fileContent: string,
		fileType: string,
	) => Promise<FirmwareUploadResponse>
	uploadRtlPerDevice: (
		fileName: string,
		fileContent: string,
		fileType: string,
		deviceIds: string[],
	) => Promise<FirmwareUploadResponse>
	clearRtlUploadStatus: () => void

	startCiBuild: (target: CiTarget, branch: CiBranch) => Promise<StartCiBuildResult>
	getCiBuildStatus: (commandId?: string) => Promise<CiBuildStatus | null>
	listCiLatestBuilds: (deviceType: CiTarget) => Promise<CiBuildArtifact[]>
	markCiBuildFinished: () => void
	uploadRtlFromCi: (macAddress: string, key: string) => Promise<FirmwareUploadResponse>

	fetchLatestFirmware: (deviceId?: string) => Promise<void>
	getDeviceFirmware: (deviceId: string) => LatestFirmware | null

	checkJobProgress: (jobId?: string, autoStopWhenComplete?: boolean) => Promise<JobProgressResponse>
	startJobProgressPolling: (jobId: string, intervalMs?: number) => void
	stopJobProgressPolling: () => void
	clearJobProgress: () => void
	getDeviceJobStatus: (macAddress: string) => JobStatus | null
	cancelQueuedJobs: () => Promise<any>
	cancelInProgressJobs: () => Promise<any>
	cancelDeviceJobs: (deviceIds: string[]) => Promise<any>
	clearDeviceJobs: (deviceIds: string[]) => Promise<any>
}

const IOT_LAMBDA_ENDPOINT = process.env.REACT_APP_IOT_LAMBDA_ENDPOINT
const CONNECTIVITY_API_URL = process.env.REACT_APP_CONNECTIVITY_API
const FIRMWARE_UPLOAD_API_URL = process.env.REACT_APP_FIRMWARE_UPLOAD_API

const iotApi = withAuth(
	axios.create({
		baseURL: IOT_LAMBDA_ENDPOINT,
		timeout: 10000,
	}),
)

const connectivityApi = withAuth(
	axios.create({
		baseURL: CONNECTIVITY_API_URL,
		timeout: 15000,
	}),
)

const firmwareApi = withAuth(
	axios.create({
		baseURL: FIRMWARE_UPLOAD_API_URL,
		timeout: 60000,
	}),
)

const getUserIdentity = () => {
	const authStore = useAuthStore.getState()

	return {
		userId: authStore.userProfile?.user_ID ?? undefined,
		email: authStore.userProfile?.email ?? undefined,
	}
}

const callIoTLambda = async (action: string, params: any = {}) => {
	try {
		const response = await iotApi.post(
			"",
			{
				action,
				...params,
			},
			{
				headers: getAuthHeaders(),
			},
		)

		return response.data
	} catch (error) {
		console.error(`Error calling IoT lambda with action ${action}:`, error)
		throw error
	}
}

const formatLambdaError = (errorData: LambdaErrorResponse): string => {
	const baseMessage = errorData.message || errorData.error || "Unknown error"

	switch (errorData.error) {
		case "INVALID_FILE_TYPE":
			return `Invalid file type: ${baseMessage}`

		case "EMPTY_FILE":
			return `Empty file: ${baseMessage}`

		case "S3_UPLOAD_FAILED":
			return `S3 upload failed: ${baseMessage}`

		case "S3_UPLOAD_UNSUCCESSFUL":
			return `S3 upload unsuccessful: ${baseMessage}`

		case "S3_VERIFICATION_FAILED": {
			const details = errorData.details
			if (Array.isArray(details) && details.length > 0) {
				const detailMessages = details
					.map((d: {type: string; message: string}) => d.message)
					.join("; ")
				return `S3 file verification failed: ${detailMessages}`
			}
			return `S3 file verification failed: ${baseMessage}`
		}

		case "UPLOAD_PARTIAL_OR_FULL_FAILURE": {
			if (errorData.failedDevices && errorData.failedDevices.length > 0) {
				const failedSummary = errorData.failedDevices
					.map((d) => `${d.macAddress} (${d.error.message})`)
					.join("; ")
				return `${baseMessage} Failed devices: ${failedSummary}`
			}
			return baseMessage
		}

		case "TYPE_MISMATCH":
			return `Device type mismatch: ${baseMessage}`

		case "BUILD_IN_PROGRESS":
			return `A build is already running: ${baseMessage}`

		case "IOT_JOB_CREATION_FAILED": {
			const detailMsg =
				typeof errorData.details === "string" ? ` Details: ${errorData.details}` : ""
			return `IoT job creation failed: ${baseMessage}${detailMsg} Files were uploaded to S3 but devices will not receive the update.`
		}

		case "UNEXPECTED_ERROR": {
			const detailMsg =
				typeof errorData.details === "string" ? ` Details: ${errorData.details}` : ""
			return `Unexpected server error: ${baseMessage}${detailMsg}`
		}

		default:
			return baseMessage
	}
}

// Actions that touch ALL devices are admin-only (the firmware Lambda refuses them with 403 too).
const ALL_DEVICES_ADMIN_ONLY = "This action affects all devices and is available to administrators only."
const isAdminUser = () => useAuthStore.getState().userProfile?.role === "admin"

// Read-only roles (viewer / viewer_all) may not change anything; the backend answers with 403 too.
const isReadOnlyUser = () => isReadOnlyRole(useAuthStore.getState().userProfile?.role)

const extractAxiosErrorMessage = (error: unknown, fallback: string): string => {
	if (axios.isAxiosError(error)) {
		if (error.response) {
			const errorData = error.response.data as LambdaErrorResponse
			if (errorData && (errorData.error || errorData.message)) {
				return formatLambdaError(errorData)
			}
			return `Server error: ${error.response.status}`
		} else if (error.request) {
			return "No response from server — check your network connection"
		} else {
			return "Request configuration error"
		}
	}
	return fallback
}

export const useDevicesStore = create<DevicesState>()(
	persist(
		(set, get) => ({
			status: "idle",
			devices: [],
			selectedDevice: null,
			error: null,
			deviceConnectivity: new Map(),
			connectivityLoading: false,
			connectivityError: null,
			firmwareUploadLoading: false,
			firmwareUploadError: null,
			firmwareUploadSuccess: null,
			firmwareErrorDismissed: false,
			rtlUploadLoading: false,
			rtlUploadError: null,
			rtlUploadSuccess: null,
			rtlErrorDismissed: false,
			ciBuildFinishedAt: null,
			latestFirmware: null,
			latestFirmwareLoading: false,
			deviceFirmwares: new Map(),
			jobProgress: null,
			jobProgressLoading: false,
			jobProgressError: null,
			currentJobId: null,
			currentJobType: null,
			jobProgressInterval: null,
			deviceUploadStatuses: new Map(),
			latestJobId: null,

			getAllDevices: async () => {
				set({status: "loading", error: null})
				try {
					const response = await callIoTLambda("devices")
					const devices = (response.devices as any[]) || []

					set({
						status: "loaded",
						devices: devices,
						error: null,
					})

					const macAddresses = devices.map((device) => device.device_uid)
					if (macAddresses.length > 0) {
						get().fetchDevicesConnectivity(macAddresses)
					}
				} catch (error) {
					console.error("GetAllDevices error:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to fetch devices")
					set({
						status: "error",
						devices: [],
						error: errorMessage,
					})
				}
			},

			getDevicesByUserId: async (userId: string) => {
				set({status: "loading", error: null})

				try {
					const response = await callIoTLambda("devices", {user_ID: userId})
					const devices = (response.devices as any[]) || []

					set({
						status: "loaded",
						devices: devices,
						error: null,
					})

					const macAddresses = devices.map((device) => device.device_uid)
					if (macAddresses.length > 0) {
						get().fetchDevicesConnectivity(macAddresses)
					}
				} catch (error) {
					console.error("GetDevicesByUserId error:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to fetch user devices")
					set({
						status: "error",
						devices: [],
						error: errorMessage,
					})
				}
			},

			getDeviceById: async (deviceId: string) => {
				set({status: "loading", error: null})
				try {
					const response = await callIoTLambda("devices", {device_uid: deviceId})
					const device = response.devices?.[0] || null

					set({
						status: "loaded",
						selectedDevice: device,
						error: null,
					})

					if (device) {
						get().fetchDevicesConnectivity([device.device_uid])
					}
				} catch (error) {
					console.error("GetDeviceById error:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to fetch device")
					set({
						status: "error",
						selectedDevice: null,
						error: errorMessage,
					})
				}
			},

			fetchDevicesConnectivity: async (macAddresses: string[]) => {
				if (macAddresses.length === 0) return

				set({connectivityLoading: true, connectivityError: null})

				try {
					const response = await connectivityApi.post(
						"",
						{
							mac_addresses: macAddresses.join(","),
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const connectivityData: DeviceConnectivity[] = response.data

					const currentConnectivity = new Map(get().deviceConnectivity)

					connectivityData.forEach((device) => {
						if (device && device.macAddress) {
							currentConnectivity.set(device.macAddress, device)
						}
					})

					set({
						deviceConnectivity: currentConnectivity,
						connectivityLoading: false,
						connectivityError: null,
					})
				} catch (error) {
					console.error("Error fetching devices connectivity:", error)
					const errorMessage = extractAxiosErrorMessage(
						error,
						"Unknown error while fetching connectivity status",
					)
					set({
						connectivityLoading: false,
						connectivityError: errorMessage,
					})
				}
			},

			getDeviceConnectionStatus: (deviceId: string) => {
				const connectivity = get().deviceConnectivity.get(deviceId)
				return connectivity?.status || "unknown"
			},

			uploadFirmware: async (fileName: string, fileContent: string, fileType: string) => {
				if (get().firmwareUploadLoading)
					return Promise.reject(new Error("Upload already in progress"))
				if (isReadOnlyUser()) {
					set({firmwareUploadError: READ_ONLY_REFUSED, firmwareUploadSuccess: null})
					return Promise.reject(new Error(READ_ONLY_REFUSED))
				}
				if (!isAdminUser()) {
					set({firmwareUploadError: ALL_DEVICES_ADMIN_ONLY, firmwareUploadSuccess: null})
					return Promise.reject(new Error(ALL_DEVICES_ADMIN_ONLY))
				}

				set({
					firmwareUploadLoading: true,
					firmwareUploadError: null,
					firmwareUploadSuccess: null,
					firmwareErrorDismissed: false,
				})

				try {
					const {userId, email} = getUserIdentity()

					const response = await firmwareApi.post(
						"",
						{
							fileName,
							fileContent,
							fileType,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result: FirmwareUploadResponse = response.data

					set({
						firmwareUploadLoading: false,
						firmwareUploadSuccess: `File "${fileName}" uploaded to S3 and job created. Waiting for all devices to apply the update...`,
						firmwareUploadError: null,
					})

					if (result.jobId) {
						set({currentJobId: result.jobId, latestJobId: result.jobId, currentJobType: "firmware"})
						get().startJobProgressPolling(result.jobId, 5000)
					}

					return result
				} catch (error) {
					console.error("Error uploading firmware:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to upload firmware file")
					set({
						firmwareUploadLoading: false,
						firmwareUploadError: errorMessage,
						firmwareUploadSuccess: null,
					})
					throw new Error(errorMessage)
				}
			},

			uploadFirmwarePerDevice: async (
				fileName: string,
				fileContent: string,
				fileType: string,
				deviceIds: string[],
			) => {
				if (get().firmwareUploadLoading)
					return Promise.reject(new Error("Upload already in progress"))
				if (isReadOnlyUser()) {
					set({firmwareUploadError: READ_ONLY_REFUSED, firmwareUploadSuccess: null})
					return Promise.reject(new Error(READ_ONLY_REFUSED))
				}

				set({
					firmwareUploadLoading: true,
					firmwareUploadError: null,
					firmwareUploadSuccess: null,
					firmwareErrorDismissed: false,
				})

				try {
					const {userId, email} = getUserIdentity()

					const response = await firmwareApi.post(
						"",
						{
							action: "perDevice",
							fileName,
							fileContent,
							fileType,
							deviceIds,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result: FirmwareUploadResponse = response.data

					set({
						firmwareUploadLoading: false,
						firmwareUploadSuccess: `File "${fileName}" uploaded to S3 and job created for ${deviceIds.length} device(s). Waiting for devices to apply the update...`,
						firmwareUploadError: null,
					})

					if (result.jobId) {
						set({currentJobId: result.jobId, latestJobId: result.jobId, currentJobType: "firmware"})
						get().startJobProgressPolling(result.jobId, 5000)
					}

					return result
				} catch (error) {
					console.error("Error uploading firmware per device:", error)
					const errorMessage = extractAxiosErrorMessage(
						error,
						"Failed to upload firmware to selected devices",
					)
					set({
						firmwareUploadLoading: false,
						firmwareUploadError: errorMessage,
						firmwareUploadSuccess: null,
					})
					throw new Error(errorMessage)
				}
			},

			uploadRtl: async (fileName: string, fileContent: string, fileType: string) => {
				if (get().rtlUploadLoading) return Promise.reject(new Error("Upload already in progress"))
				if (isReadOnlyUser()) {
					set({rtlUploadError: READ_ONLY_REFUSED, rtlUploadSuccess: null})
					return Promise.reject(new Error(READ_ONLY_REFUSED))
				}
				if (!isAdminUser()) {
					set({rtlUploadError: ALL_DEVICES_ADMIN_ONLY, rtlUploadSuccess: null})
					return Promise.reject(new Error(ALL_DEVICES_ADMIN_ONLY))
				}

				set({
					rtlUploadLoading: true,
					rtlUploadError: null,
					rtlUploadSuccess: null,
					rtlErrorDismissed: false,
				})

				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{
							action: "rtl",
							fileName,
							fileContent,
							fileType,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result: FirmwareUploadResponse = response.data

					set({
						rtlUploadLoading: false,
						rtlUploadSuccess: `RTL file "${fileName}" uploaded to S3 and job created. Waiting for all devices to apply the update...`,
						rtlUploadError: null,
					})

					if (result.jobId) {
						set({currentJobId: result.jobId, latestJobId: result.jobId, currentJobType: "rtl"})
						get().startJobProgressPolling(result.jobId, 5000)
					}

					return result
				} catch (error) {
					console.error("Error uploading RTL:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to upload RTL file")
					set({
						rtlUploadLoading: false,
						rtlUploadError: errorMessage,
						rtlUploadSuccess: null,
					})
					throw new Error(errorMessage)
				}
			},

			uploadRtlPerDevice: async (
				fileName: string,
				fileContent: string,
				fileType: string,
				deviceIds: string[],
			) => {
				if (get().rtlUploadLoading) return Promise.reject(new Error("Upload already in progress"))
				if (isReadOnlyUser()) {
					set({rtlUploadError: READ_ONLY_REFUSED, rtlUploadSuccess: null})
					return Promise.reject(new Error(READ_ONLY_REFUSED))
				}

				set({
					rtlUploadLoading: true,
					rtlUploadError: null,
					rtlUploadSuccess: null,
					rtlErrorDismissed: false,
				})

				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{
							action: "rtlPerDevice",
							fileName,
							fileContent,
							fileType,
							deviceIds,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result: FirmwareUploadResponse = response.data

					set({
						rtlUploadLoading: false,
						rtlUploadSuccess: `RTL file "${fileName}" uploaded to S3 and job created for ${deviceIds.length} device(s). Waiting for devices to apply the update...`,
						rtlUploadError: null,
					})

					if (result.jobId) {
						set({currentJobId: result.jobId, latestJobId: result.jobId, currentJobType: "rtl"})
						get().startJobProgressPolling(result.jobId, 5000)
					}

					return result
				} catch (error) {
					console.error("Error uploading RTL per device:", error)
					const errorMessage = extractAxiosErrorMessage(
						error,
						"Failed to upload RTL file to selected devices",
					)
					set({
						rtlUploadLoading: false,
						rtlUploadError: errorMessage,
						rtlUploadSuccess: null,
					})
					throw new Error(errorMessage)
				}
			},

			startCiBuild: async (target: CiTarget, branch: CiBranch) => {
				if (isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
				if (!CI_TARGETS.includes(target) || !CI_BRANCHES.includes(branch)) {
					throw new Error("Invalid build target or branch")
				}
				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{action: "ciBuild", target, branch, user_ID: userId, email},
						{headers: getAuthHeaders()},
					)
					const data = response.data || {}
					return {
						alreadyRunning: false,
						commandId: data.commandId,
						target,
						branch,
						status: data.status || "Pending",
					}
				} catch (error) {
					// 409: a console build is still running; the caller attaches to that build.
					if (
						axios.isAxiosError(error) &&
						error.response?.status === 409 &&
						(error.response.data as any)?.error === "BUILD_IN_PROGRESS"
					) {
						const data = error.response.data as any
						return {
							alreadyRunning: true,
							commandId: typeof data.commandId === "string" ? data.commandId : null,
							message: data.message || "A build is already running",
						}
					}
					console.error("Error starting CI build:", error)
					throw new Error(extractAxiosErrorMessage(error, "Failed to start the CI build"))
				}
			},

			getCiBuildStatus: async (commandId?: string) => {
				try {
					const response = await firmwareApi.post(
						"",
						commandId ? {action: "ciBuildStatus", commandId} : {action: "ciBuildStatus"},
						{headers: getAuthHeaders()},
					)
					const build = response.data?.build
					if (!build || !build.commandId) return null
					const tail = build.outputTail
					return {
						commandId: build.commandId,
						target: build.target ?? "",
						branch: build.branch ?? "",
						status: build.status ?? "Unknown",
						requestedAt: build.requestedAt ?? null,
						finishedAt: build.finishedAt ?? null,
						outputTail: Array.isArray(tail) ? tail.join("\n") : typeof tail === "string" ? tail : "",
					}
				} catch (error) {
					console.error("Error fetching CI build status:", error)
					throw new Error(extractAxiosErrorMessage(error, "Failed to fetch the CI build status"))
				}
			},

			listCiLatestBuilds: async (deviceType: CiTarget) => {
				if (!CI_TARGETS.includes(deviceType)) throw new Error("Invalid device type")
				try {
					const response = await firmwareApi.post(
						"",
						{action: "ciLatestBuilds", deviceType},
						{headers: getAuthHeaders()},
					)
					const builds: CiBuildArtifact[] = Array.isArray(response.data?.builds)
						? response.data.builds
						: []
					// Newest first (the server sorts too; keep the order stable here regardless).
					return [...builds].sort((a, b) => (a.builtAt < b.builtAt ? 1 : a.builtAt > b.builtAt ? -1 : 0))
				} catch (error) {
					console.error("Error listing CI builds:", error)
					throw new Error(extractAxiosErrorMessage(error, "Failed to list CI builds"))
				}
			},

			markCiBuildFinished: () => set({ciBuildFinishedAt: Date.now()}),

			uploadRtlFromCi: async (macAddress: string, key: string) => {
				if (get().rtlUploadLoading) return Promise.reject(new Error("Upload already in progress"))
				if (isReadOnlyUser()) {
					set({rtlUploadError: READ_ONLY_REFUSED, rtlUploadSuccess: null})
					return Promise.reject(new Error(READ_ONLY_REFUSED))
				}

				set({
					rtlUploadLoading: true,
					rtlUploadError: null,
					rtlUploadSuccess: null,
					rtlErrorDismissed: false,
				})

				const buildName = key.split("/").pop() || key
				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{
							action: "rtlPerDeviceFromCi",
							macAddress,
							key,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result: FirmwareUploadResponse = response.data

					set({
						rtlUploadLoading: false,
						rtlUploadSuccess: `CI build "${buildName}" copied to S3 and job created for 1 device(s) (${macAddress}). Waiting for the device to apply the update...`,
						rtlUploadError: null,
					})

					if (result.jobId) {
						set({currentJobId: result.jobId, latestJobId: result.jobId, currentJobType: "rtl"})
						get().startJobProgressPolling(result.jobId, 5000)
					}

					return result
				} catch (error) {
					console.error("Error uploading RTL from CI build:", error)
					const errorMessage = extractAxiosErrorMessage(
						error,
						"Failed to send the CI build to the selected device",
					)
					set({
						rtlUploadLoading: false,
						rtlUploadError: errorMessage,
						rtlUploadSuccess: null,
					})
					throw new Error(errorMessage)
				}
			},

			fetchLatestFirmware: async (deviceId?: string) => {
				set({latestFirmwareLoading: true})

				try {
					const params = deviceId ? {device_uid: deviceId} : {}
					const response = await callIoTLambda("latest-firmware", params)

					if (deviceId) {
						const firmware = response.firmware as LatestFirmware | null
						if (firmware) {
							const currentDeviceFirmwares = new Map(get().deviceFirmwares)
							currentDeviceFirmwares.set(deviceId, firmware)
							set({
								deviceFirmwares: currentDeviceFirmwares,
								latestFirmwareLoading: false,
							})
						} else {
							set({latestFirmwareLoading: false})
						}
					} else {
						const deviceFirmwares = response.deviceFirmwares as Record<string, LatestFirmware>
						const generalFirmware = response.generalFirmware as LatestFirmware | null

						const firmwaresMap = new Map<string, LatestFirmware>()
						if (deviceFirmwares) {
							Object.entries(deviceFirmwares).forEach(([mac, firmware]) => {
								firmwaresMap.set(mac, firmware)
							})
						}

						set({
							deviceFirmwares: firmwaresMap,
							latestFirmware: generalFirmware,
							latestFirmwareLoading: false,
						})
					}
				} catch (error) {
					console.error("Error fetching latest firmware:", error)
					set({
						latestFirmware: null,
						latestFirmwareLoading: false,
					})
				}
			},

			getDeviceFirmware: (deviceId: string) => {
				const deviceFirmware = get().deviceFirmwares.get(deviceId)
				if (deviceFirmware) return deviceFirmware
				return get().latestFirmware
			},

			checkJobProgress: async (jobId?: string, autoStopWhenComplete: boolean = true) => {
				set({
					jobProgressLoading: true,
					jobProgressError: null,
				})

				const jobToCheck = jobId || get().latestJobId

				if (!jobToCheck) {
					set({
						jobProgressLoading: false,
						jobProgressError: "No job ID available",
					})
					return {
						jobId: "",
						checkedAt: new Date().toISOString(),
						summary: {
							total: 0,
							queued: 0,
							inProgress: 0,
							succeeded: 0,
							failed: 0,
							notFound: 0,
							error: 0,
						},
						devices: [],
					}
				}

				try {
					const {userId, email} = getUserIdentity()

					const response = await firmwareApi.post(
						"",
						{
							action: "checkProgress",
							jobId: jobToCheck,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result: JobProgressResponse = response.data

					const statusMap = new Map<string, JobStatus>()
					result.devices.forEach((device) => {
						statusMap.set(device.macAddress, device)
					})

					set({
						jobProgress: result,
						deviceUploadStatuses: statusMap,
						jobProgressLoading: false,
						jobProgressError: null,
					})

					if (autoStopWhenComplete) {
						const {summary} = result
						const hasActiveJobs = summary.queued > 0 || summary.inProgress > 0

						if (!hasActiveJobs) {
							get().stopJobProgressPolling()

							const jobType = get().currentJobType
							const label = jobType === "rtl" ? "RTL" : "Firmware"

							if (summary.failed > 0 && summary.succeeded === 0) {
								const errorMsg = `${label} update failed: ${summary.failed} device(s) failed, ${summary.succeeded} succeeded.`
								if (jobType === "rtl") {
									if (!get().rtlErrorDismissed) {
										set({rtlUploadError: errorMsg, rtlUploadSuccess: null})
									}
								} else {
									if (!get().firmwareErrorDismissed) {
										set({firmwareUploadError: errorMsg, firmwareUploadSuccess: null})
									}
								}
							} else if (summary.failed > 0 && summary.succeeded > 0) {
								const partialMsg = `${label} update partially completed: ${summary.succeeded} device(s) succeeded, ${summary.failed} failed.`
								if (jobType === "rtl") {
									if (!get().rtlErrorDismissed) {
										set({rtlUploadSuccess: partialMsg})
									}
								} else {
									if (!get().firmwareErrorDismissed) {
										set({firmwareUploadSuccess: partialMsg})
									}
								}
							} else if (summary.succeeded > 0) {
								const successMsg = `${label} update completed successfully: ${summary.succeeded} device(s) updated.`
								if (jobType === "rtl") {
									set({rtlUploadSuccess: successMsg, rtlUploadError: null})
								} else {
									set({firmwareUploadSuccess: successMsg, firmwareUploadError: null})
								}
							}
						}
					}

					return result
				} catch (error) {
					console.error("Error checking job progress:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to check job progress")
					set({
						jobProgressLoading: false,
						jobProgressError: errorMessage,
					})
					throw new Error(errorMessage)
				}
			},

			startJobProgressPolling: (jobId: string, intervalMs: number = 5000) => {
				const state = get()

				if (state.jobProgressInterval) {
					clearInterval(state.jobProgressInterval)
				}

				set({currentJobId: jobId})

				get().checkJobProgress(jobId, false)

				const interval = setInterval(() => {
					const currentState = get()
					if (currentState.currentJobId === jobId) {
						get().checkJobProgress(jobId, true)
					}
				}, intervalMs)

				set({jobProgressInterval: interval})
			},

			stopJobProgressPolling: () => {
				const state = get()

				if (state.jobProgressInterval) {
					clearInterval(state.jobProgressInterval)
					set({
						jobProgressInterval: null,
						currentJobId: null,
						currentJobType: null,
					})
				}
			},

			cancelQueuedJobs: async () => {
				if (isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
				if (!isAdminUser()) throw new Error(ALL_DEVICES_ADMIN_ONLY)
				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{
							action: "cancelAllQueued",
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result = response.data

					if (result.summary && result.summary.canceled > 0) {
						console.log(`Successfully canceled ${result.summary.canceled} queued job executions`)
					} else if (result.message) {
						console.log(result.message)
					}

					return result
				} catch (error) {
					console.error("Error canceling queued jobs:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to cancel queued jobs")
					throw new Error(errorMessage)
				}
			},

			cancelInProgressJobs: async () => {
				if (isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
				if (!isAdminUser()) throw new Error(ALL_DEVICES_ADMIN_ONLY)
				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{
							action: "cancelAllInProgress",
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					const result = response.data

					if (result.summary && result.summary.canceled > 0) {
						console.log(
							`Successfully canceled ${result.summary.canceled} in-progress job executions`,
						)
					} else if (result.message) {
						console.log(result.message)
					}

					return result
				} catch (error) {
					console.error("Error canceling in-progress jobs:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to cancel in-progress jobs")
					throw new Error(errorMessage)
				}
			},

			cancelDeviceJobs: async (deviceIds: string[]) => {
				if (isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{
							action: "cancelDeviceJobs",
							deviceIds,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					return response.data
				} catch (error) {
					console.error("Error canceling device jobs:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to cancel device jobs")
					throw new Error(errorMessage)
				}
			},

			clearDeviceJobs: async (deviceIds: string[]) => {
				if (isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
				try {
					const {userId, email} = getUserIdentity()
					const response = await firmwareApi.post(
						"",
						{
							action: "clearDeviceJobs",
							deviceIds,
							user_ID: userId,
							email,
						},
						{
							headers: getAuthHeaders(),
						},
					)

					return response.data
				} catch (error) {
					console.error("Error clearing device jobs:", error)
					const errorMessage = extractAxiosErrorMessage(error, "Failed to clear device jobs")
					throw new Error(errorMessage)
				}
			},

			getDeviceJobStatus: (macAddress: string) => {
				const deviceStatus = get().deviceUploadStatuses.get(macAddress)
				return deviceStatus || null
			},

			clearJobProgress: () => {
				const state = get()

				if (state.jobProgressInterval) {
					clearInterval(state.jobProgressInterval)
				}

				set({
					jobProgress: null,
					jobProgressError: null,
					currentJobId: null,
					currentJobType: null,
					jobProgressInterval: null,
					deviceUploadStatuses: new Map(),
				})
			},

			clearFirmwareUploadStatus: () => {
				get().stopJobProgressPolling()
				set({
					firmwareUploadError: null,
					firmwareUploadSuccess: null,
					firmwareErrorDismissed: true,
				})
			},

			clearRtlUploadStatus: () => {
				get().stopJobProgressPolling()
				set({
					rtlUploadError: null,
					rtlUploadSuccess: null,
					rtlErrorDismissed: true,
				})
			},

			setSelectedDevice: (device: Device | null) => {
				set({selectedDevice: device})
			},

			clearDevices: () => {
				set({
					status: "idle",
					devices: [],
					selectedDevice: null,
					error: null,
				})
			},

			clearConnectivity: () => {
				set({
					deviceConnectivity: new Map(),
					connectivityError: null,
				})
			},

			clearError: () => {
				set({
					error: null,
					connectivityError: null,
					firmwareUploadError: null,
					rtlUploadError: null,
					jobProgressError: null,
				})
			},
		}),
		{
			name: "devices-storage",
			partialize: (state) => ({
				devices: state.devices,
				selectedDevice: state.selectedDevice,
				latestJobId: state.latestJobId,
			}),
		},
	),
)

// Job progress polling outlives the dashboard; stop it on logout so it doesn't keep hitting the API without a token
useAuthStore.subscribe((state) => {
	if (!state.accessToken) {
		useDevicesStore.getState().stopJobProgressPolling()
	}
})

// devices / selectedDevice / latestJobId are persisted in localStorage: drop them (and the in-memory
// per-user data) when the user changes, so the next user on this browser never sees the previous
// user's devices or job.
onConsoleUserChange(() => {
	const store = useDevicesStore.getState()
	store.clearJobProgress()
	store.clearDevices()
	store.clearConnectivity()
	useDevicesStore.setState({
		latestJobId: null,
		latestFirmware: null,
		deviceFirmwares: new Map(),
		firmwareUploadError: null,
		firmwareUploadSuccess: null,
		rtlUploadError: null,
		rtlUploadSuccess: null,
	})
})
